import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import GrowVerticalIcon from '@atlaskit/icon/core/grow-vertical';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import { token } from '@atlaskit/tokens';
import { useOptionalAnnouncements } from '@atlassian/navigation-accessibility/use-optional-announcements';

import type { NavItemReorderingProps } from '../../../types';

import messages from './messages';

export function NavItemReorderingMenu({
	TriggerComponent = ButtonMenuItem,
	canMoveUp,
	canMoveDown,
	moveUp,
	moveDown,
	moveToTop,
	moveToBottom,
	onOpenChange,
	itemLabel,
	itemPosition,
	itemCount,
}: NavItemReorderingProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const announcementContext = useOptionalAnnouncements();
	const label = itemLabel ?? '';
	const total = itemCount ?? 0;
	const isOnly = !canMoveUp && !canMoveDown;
	const [open, setOpen] = useState(false);
	const listItemRef = useRef<HTMLDivElement>(null);

	const onOpenChangeInternal = useCallback(
		(args: OnOpenChangeArgs) => {
			setOpen(args.isOpen);
			onOpenChange?.(args);
		},
		[onOpenChange],
	);

	useEffect(() => {
		// Remove role="listitem" from the root component within ButtonMenuItem.
		// We add our own wrapper with role around the whole thing, to ensure that the menu is rendered inside the listitem/menuitem semantically.
		// Otherwise, it would be a sibling to it without the appropriate role, which is both an a11y violation and doesn't make semantic sense.
		// Also, this allows us to account for the expected parent roles of all supported trigger types.
		if (TriggerComponent === ButtonMenuItem) {
			if (listItemRef.current) {
				listItemRef.current.removeAttribute('role');
			}
		}
	}, [TriggerComponent]);

	/**
	 * Set wrapper role based on the default semantics of the trigger component.
	 * This is just so we can shuffle around where the role attribute is defined
	 * to ensure semantics of the whole component are correct when the reordering menu is fully open.
	 * We aren't messing with the roles themselves at this point
	 * (e.g., making a ButtonMenuItem a "menuitem" not a "listitem"), though we could if there is a need to in the future.
	 */
	const wrapperRole = useMemo(() => {
		if (TriggerComponent === ButtonMenuItem) {
			return 'listitem';
		}

		return undefined;
	}, [TriggerComponent]);

	const handleMoveToTop = useCallback(() => {
		moveToTop();
		announcementContext?.setAnnouncement(
			formatMessage(messages.announcementMovedToTop, { itemLabel: label, position: 1, total }),
		);
	}, [moveToTop, announcementContext, formatMessage, label, total]);

	const handleMoveUp = useCallback(() => {
		moveUp();
		announcementContext?.setAnnouncement(
			formatMessage(messages.announcementMovedUp, {
				itemLabel: label,
				position: (itemPosition ?? 1) - 1,
				total,
			}),
		);
	}, [moveUp, announcementContext, formatMessage, label, itemPosition, total]);

	const handleMoveDown = useCallback(() => {
		moveDown();
		announcementContext?.setAnnouncement(
			formatMessage(messages.announcementMovedDown, {
				itemLabel: label,
				position: (itemPosition ?? 1) + 1,
				total,
			}),
		);
	}, [moveDown, announcementContext, formatMessage, label, itemPosition, total]);

	const handleMoveToBottom = useCallback(() => {
		moveToBottom();
		announcementContext?.setAnnouncement(
			formatMessage(messages.announcementMovedToBottom, {
				itemLabel: label,
				position: total,
				total,
			}),
		);
	}, [moveToBottom, announcementContext, formatMessage, label, total]);

	return (
		<div role={wrapperRole}>
			<DropdownMenu
				shouldRenderToParent
				spacing="compact"
				placement="right-start"
				trigger={({ triggerRef, ...triggerProps }: CustomTriggerProps) => {
					return (
						<TriggerComponent
							elemBefore={<GrowVerticalIcon label="" />}
							elemAfter={
								<ChevronRightIcon color={token('color.icon.subtle')} label="" size="small" />
							}
							{...triggerProps}
							ref={triggerRef}
							isDisabled={isOnly}
							listItemRef={listItemRef}
						>
							{formatMessage(messages.reorderingMenuTriggerLabel)}
						</TriggerComponent>
					);
				}}
				onOpenChange={onOpenChangeInternal}
				isOpen={isOnly ? false : open}
			>
				<DropdownItemGroup>
					<DropdownItem isDisabled={!canMoveUp} onClick={handleMoveToTop}>
						{formatMessage(messages.moveToTop)}
					</DropdownItem>
					<DropdownItem isDisabled={!canMoveUp} onClick={handleMoveUp}>
						{formatMessage(messages.moveUp)}
					</DropdownItem>
					<DropdownItem isDisabled={!canMoveDown} onClick={handleMoveDown}>
						{formatMessage(messages.moveDown)}
					</DropdownItem>
					<DropdownItem isDisabled={!canMoveDown} onClick={handleMoveToBottom}>
						{formatMessage(messages.moveToBottom)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
		</div>
	);
}
